import React, { Component } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { API_URL } from '../../../settings/local_settings';
import { APIClient } from '../../../utils/APIClient';
import { isOk } from '../../../utils/apiResponse';
import { ScreenTracker } from '../../../utils/ScreenTracker';
import { Spinner } from '../../Spinner';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatSubscriptionDate } from './subscriptionDates';
import { openSupportRequest } from './paid/subscriptionRequests';
import { formatAmount, formatLongDate } from './paid/subscriptionPageModel';
import {
    OFFER_TIMEOUT_MS,
    offerFromAccepted,
    offerFromQuote,
    offerMonthsText,
    savingsOf,
    splitAround,
} from './retentionOffer';
import './CancelSubscriptionModal.scss';

/**
 * Motivos de baja. El `value` viaja al backend y tiene que coincidir EXACTAMENTE con
 * `CancellationRequest.REASON_CHOICES` — hay un test guardián del lado del backend.
 *
 * `offRamp` es la salida alternativa que se le ofrece a la agencia ANTES de confirmar, elegida por
 * el motivo: es la razón de ser del paso 2. Hasta 2026-09-01 el motivo se preguntaba junto con la
 * confirmación, o sea DESPUÉS de que la persona ya había decidido — servía para el reporte de
 * churn, no para retener. `null` = no hay off-ramp honesto que ofrecer (se cierra la agencia, o se
 * mudan a otra herramienta): inventar fricción ahí sólo molesta.
 *
 * `offer: true` = con ese motivo se pregunta si la agencia puede recibir el descuento de retención
 * (UD-17, D-2: el precio y el cambio de herramienta). Si no puede, sigue su `offRamp` de siempre.
 */
const REASONS = [
    { value: 'too_expensive', labelKey: 'agency_subscription.reason_too_expensive', offRamp: 'plans', offer: true },
    { value: 'not_using', labelKey: 'agency_subscription.reason_not_using', offRamp: 'talk' },
    { value: 'missing_features', labelKey: 'agency_subscription.reason_missing_features', offRamp: 'talk' },
    { value: 'switching_tool', labelKey: 'agency_subscription.reason_switching_tool', offRamp: null, offer: true },
    { value: 'closing_agency', labelKey: 'agency_subscription.reason_closing_agency', offRamp: null },
    { value: 'other', labelKey: 'agency_subscription.other', offRamp: 'talk' },
];

// El título de la oferta según el motivo (copy.md §2).
const OFFER_TITLE_KEYS = {
    too_expensive: 'agency_subscription.retention_title_price',
    switching_tool: 'agency_subscription.retention_title_switching',
};

const STEP_REASON = 1;
const STEP_CONFIRM = 2;
// Preguntando si hay oferta: un Spinner en el cuerpo, nunca un texto «Cargando».
const STEP_CHECKING = 3;
const STEP_OFFER = 4;
const STEP_APPLIED = 5;

// Los valores interpolados (montos, fechas, meses, el motivo) viajan como TEXTO: sin esto i18next los
// escapa a entidades HTML.
const RAW = { escapeValue: false };
const AMOUNT_MARKER = '\u0000amount\u0000';

/** «USD 100» con espacio duro: a lo angosto, la moneda y el número no se separan en dos renglones. */
function moneyText(value, currency, lang) {
    return formatAmount(value, currency, lang).replace(' ', '\u00a0');
}

function recordEvent(name) {
    ScreenTracker.recordEvent(name);
}

function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

class CancelSubscriptionModal extends Component {
    constructor(props) {
        super(props);
        this.state = this._initialState();
        // Dos contadores, para descartar respuestas viejas. `_openSeq` es la apertura del modal: la aceptación
        // y las líneas de un modal que ya se cerró no tocan nada. `_offerSeq` es además cada consulta de la
        // oferta: la de un paso que ya se dejó (los 8 s) tampoco. Son dos porque consultar la oferta no puede
        // invalidar el pedido de las líneas, que sigue en vuelo.
        this._openSeq = 0;
        this._offerSeq = 0;
        this._offerTimer = null;
        this.toggle = this.toggle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleContinue = this.handleContinue.bind(this);
        this.handleAcceptOffer = this.handleAcceptOffer.bind(this);
        this.handleDeclineOffer = this.handleDeclineOffer.bind(this);
        this.openRetentionChat = this.openRetentionChat.bind(this);
        this.goToPlans = this.goToPlans.bind(this);
        this.talkToSupport = this.talkToSupport.bind(this);
    }

    _initialState() {
        return {
            isOpen: false,
            step: STEP_REASON,
            sending: false,
            sent: false,
            reason: '',
            comments: '',
            // La oferta que se está mostrando (normalizada por `retentionOffer.js`), o null.
            offer: null,
            accepting: false,
            acceptError: false,
            quoteChanged: false,
            appliedOffer: null,
            // La agencia vio la oferta y siguió: la confirmación no le ofrece otra salida (M5).
            declinedOffer: false,
            // `{lines, clients}` de `agency/clients/summary/`, o null si no llegó.
            lossLines: null,
        };
    }

    componentWillUnmount() {
        this._clearOfferTimer();
        this._openSeq += 1;
        this._offerSeq += 1;
    }

    _clearOfferTimer() {
        if (this._offerTimer) {
            clearTimeout(this._offerTimer);
            this._offerTimer = null;
        }
    }

    toggle() {
        const opening = !this.state.isOpen;
        if (opening && this.props.onOpen) {
            this.props.onOpen();
        }
        this._clearOfferTimer();
        this._openSeq += 1;
        this._offerSeq += 1;
        this.setState({ ...this._initialState(), isOpen: opening }, () => {
            if (opening) this._loadLossLines();
        });
    }

    /**
     * Las líneas de seguimiento que se pierden, para la confirmación (M5). Se piden al abrir, así llegan antes
     * de que la persona pase el paso del motivo. Sólo para quien ve TODOS los clientes: `clients/summary/` le
     * devuelve a un miembro sin permisos sólo los suyos, y la agencia pierde todas. Si no llega, no se dibuja.
     */
    _loadLossLines() {
        const { subscriptionData } = this.props;
        if (!subscriptionData || subscriptionData.is_agency_admin !== true) return;
        const seq = this._openSeq;
        new APIClient(API_URL + 'agency/clients/summary/').call('GET').then((response) => {
            if (seq !== this._openSeq || !isOk(response) || !Array.isArray(response.data)) return;
            const withLines = response.data.filter((client) => isNumber(client.business_units_count)
                && client.business_units_count > 0);
            const lines = withLines.reduce((total, client) => total + client.business_units_count, 0);
            this.setState({ lossLines: { lines, clients: withLines.length } });
        });
    }

    goToPlans() {
        this.toggle();
        if (this.props.onGoToPlans) {
            this.props.onGoToPlans();
        }
    }

    /**
     * Abre el chat de soporte con el motivo ya escrito, en vez de un `mailto:`.
     *
     * Si la agencia tiene el chat propio, abre la burbuja con el texto pre-cargado (y la
     * conversación aterriza en el inbox del CC); si no cargó, el pedido sale por WhatsApp con el
     * número de agencia (`openSupportRequest`). El `mailto:` que había antes dependía del cliente de
     * mail del usuario y la respuesta caía fuera del inbox del equipo — justo en el momento de
     * máxima intención de retención.
     *
     * Se cierra el modal: el panel del chat se monta por encima y dejar los dos abiertos tapa la
     * conversación que se acaba de abrir.
     */
    talkToSupport() {
        const { t } = this.props;
        const reason = this._selectedReason();
        const reasonLabel = reason ? t(reason.labelKey) : '';
        this.toggle();
        // `escapeValue: false` por lo mismo que las fechas: el motivo se interpola y el mensaje
        // viaja al chat como TEXTO plano. En inglés `reason_not_using` es "I'm not using the
        // platform" y el escapado por default de i18next le manda al equipo un `I&#39;m`.
        // `openSupportRequest` y no `showNewMessage`: sin el chat propio cargado, éste caía a Intercom,
        // dado de baja el 04/10, y el botón cerraba el modal sin abrir nada (C-bd-fe-03). Así, si el chat
        // no cargó, el pedido sale por WhatsApp con el número de agencia, como el error de la oferta.
        openSupportRequest(t, t('agency_subscription.cancel_offramp_talk_message', {
            reason: reasonLabel,
            interpolation: { escapeValue: false }
        }));
    }

    /**
     * «Continuar» del paso del motivo. Con un motivo de la oferta (`offer: true`) primero se pregunta si la
     * agencia puede recibirla; con cualquier otro, directo a la confirmación de siempre, sin pedir nada.
     */
    handleContinue() {
        const reason = this._selectedReason();
        if (!reason) return;
        recordEvent(`cancel_reason_${reason.value}`);
        if (!reason.offer) {
            this.setState({ step: STEP_CONFIRM });
            return;
        }
        this._checkOffer(reason.value);
    }

    /**
     * `GET retention-offer/` (contrato §2.3). Con `eligible: true` y una oferta completa, el paso de la oferta;
     * con `eligible: false`, un error, el 404 del Backend viejo o pasados 8 s, la confirmación de siempre.
     */
    _checkOffer(reason) {
        this._clearOfferTimer();
        const seq = ++this._offerSeq;
        this.setState({ step: STEP_CHECKING, offer: null, quoteChanged: false, acceptError: false });
        this._offerTimer = setTimeout(() => this._resolveOffer(seq, null), OFFER_TIMEOUT_MS);
        new APIClient(API_URL + 'agency/subscription/retention-offer/?reason=' + encodeURIComponent(reason))
            .call('GET')
            .then((response) => {
                const data = isOk(response) ? response.data : null;
                this._resolveOffer(seq, data && data.eligible === true ? offerFromQuote(data.offer) : null);
            })
            .catch(() => this._resolveOffer(seq, null));
    }

    _resolveOffer(seq, offer) {
        // Una respuesta que llega tarde (después de los 8 s, o con el modal cerrado) no mueve nada.
        if (seq !== this._offerSeq || this.state.step !== STEP_CHECKING) return;
        this._clearOfferTimer();
        if (offer) {
            recordEvent('retention_offer_shown');
            this.setState({ step: STEP_OFFER, offer });
        } else {
            this.setState({ step: STEP_CONFIRM });
        }
    }

    /**
     * «Quedarme con el descuento» (contrato §2.4). El evento de aceptación cuenta las que QUEDARON aplicadas:
     * un reintento o un quote que cambió no la cuentan dos veces.
     */
    handleAcceptOffer() {
        const { offer, reason, comments } = this.state;
        if (!offer) return;
        const seq = this._openSeq;
        // `acceptError` no se limpia acá: al reintentar, el error queda a la vista con el spinner en «Reintentar»
        // hasta que llegue la respuesta. Si se limpiara, volvía la oferta entera y el modal saltaba de alto.
        this.setState({ accepting: true });
        new APIClient(API_URL + 'agency/subscription/retention-offer/accept/')
            // El 502 `TESLA_UNAVAILABLE` es un error tipado: el Backend ya le escribe al equipo. No va a Sentry.
            .call('POST', { reason, comments, starts_on: offer.startsOn }, { expectedServerStatuses: [502] })
            .then((response) => this._handleAcceptResponse(seq, response))
            .catch(() => this._handleAcceptResponse(seq, null));
    }

    _handleAcceptResponse(seq, response) {
        const closed = seq !== this._openSeq;
        if (isOk(response)) {
            const accepted = response.data ? response.data.retention_offer : null;
            recordEvent('retention_offer_accept');
            // El modal se cerró mientras aceptaba (la X o Escape): el descuento quedó aplicado igual, así que la
            // pantalla se tiene que enterar. Si no, la tarjeta seguía sin él y el link volvía a la baja de una
            // agencia ya retenida (C-bd-fe-02). Lo que no se toca es el estado de un modal cerrado.
            if (!closed) {
                this.setState({
                    step: STEP_APPLIED,
                    accepting: false,
                    acceptError: false,
                    appliedOffer: offerFromAccepted(accepted) || this.state.offer,
                });
            }
            // La pantalla vuelve a pedir la suscripción y el historial: la tarjeta del plan pasa a decir el
            // descuento (M1) sin que la agencia recargue.
            if (this.props.onRetentionAccepted) {
                this.props.onRetentionAccepted(accepted || null);
            }
            return;
        }
        // Un error o un quote nuevo con el modal cerrado no tienen a quién mostrarse.
        if (closed) return;
        if (response && response.status === 'RETENTION_QUOTE_CHANGED') {
            // Tesla recalculó (por ejemplo, se cerró el mes): la oferta nueva viaja en el cuerpo del 409, que
            // `APIClient` deja en `raw_body`. Se muestra para que la acepte de nuevo con los meses a la vista.
            const body = response.raw_body && response.raw_body.data;
            const fresh = offerFromQuote(body && body.offer);
            if (fresh) {
                recordEvent('retention_offer_quote_changed');
                this.setState({ offer: fresh, quoteChanged: true, accepting: false, acceptError: false });
                return;
            }
        }
        recordEvent('retention_offer_error');
        this.setState({ accepting: false, acceptError: true });
    }

    /** «Seguir con la baja»: a la confirmación, sin otra salida en el camino (M5). */
    handleDeclineOffer() {
        recordEvent('retention_offer_decline');
        this.setState({ step: STEP_CONFIRM, declinedOffer: true });
    }

    /**
     * «Abrir el chat» del error al aplicar: el chat de soporte con el pedido escrito y, si el chat no cargó,
     * WhatsApp con el número de agencia (`openSupportRequest`, el mismo camino que el cupo de cuentas de
     * UD-15). Nunca `showNewMessage`, que caería a Intercom, apagado. Se cierra el modal por lo mismo que en
     * `talkToSupport`: el panel del chat se monta encima.
     */
    openRetentionChat() {
        const { t } = this.props;
        const reason = this._selectedReason();
        const message = t('agency_subscription.retention_chat_message', {
            reason: reason ? t(reason.labelKey) : '',
            interpolation: RAW,
        });
        this.toggle();
        openSupportRequest(t, message);
    }

    handleSubmit(e) {
        if (e) {
            e.preventDefault();
        }
        const { t } = this.props;
        recordEvent('cancel_confirm');
        this.setState({ sending: true });

        new APIClient(API_URL + 'agency/subscription/cancel/')
            .call('POST', {
                reason: this.state.reason,
                comments: this.state.comments
            })
            .then((data) => {
                if (data.status === 'OK') {
                    this.setState({ sending: false, sent: true });
                    // La sección de arriba pasa a mostrar el estado de la solicitud en lugar del
                    // botón. Sin esto, cerrar el modal devuelve la pantalla al estado anterior y
                    // el pedido se vuelve invisible hasta el próximo F5.
                    if (this.props.onCancellationRequested) {
                        this.props.onCancellationRequested(data.data && data.data.cancellation_request);
                    }
                } else {
                    this.setState({ sending: false });
                    // NO se interpola `data.error_message`: es el patrón que este mismo frente
                    // prohíbe en `CSVFileInput`/`AddAdAccountForm` porque el body del backend
                    // puede filtrar info de otra agencia. Y toast, no `alert()`: es lo que usa el
                    // resto de la pantalla (`AgencySubscriptionContent.refreshBilling`).
                    toast.error(t('agency_subscription.cancel_error'));
                }
            });
    }

    _selectedReason() {
        return REASONS.find((r) => r.value === this.state.reason) || null;
    }

    /** Fecha hasta la que la suscripción sigue paga, YA FORMATEADA, o null si no tiene fin
     * cargado. El backend la manda como `YYYY-MM-DD` y el bloque de la sección la muestra como
     * `15/10/2026`: interpolarla cruda dejaba dos formatos de fecha en la misma pantalla. */
    _validTill() {
        const { subscriptionData } = this.props;
        const raw = (subscriptionData && subscriptionData.subscription_valid_till) || null;
        return raw ? formatSubscriptionDate(raw) : null;
    }

    /** `ad_account_linked_by_agency_id` trae una entrada por agencia: más de una = suscripción compartida. */
    _isSharedSubscription() {
        const { subscriptionData } = this.props;
        return Object.keys(subscriptionData.ad_account_linked_by_agency_id || {}).length > 1;
    }

    /**
     * Las cuentas que pierde ESTA agencia. Con la suscripción para ella sola, las de `usage` (UD-16): el mismo
     * número que muestra el medidor de la pantalla. Con una compartida, SÓLO las suyas: `usage` suma las de
     * todas las agencias de la suscripción, y sumarlas le prometía a una que iba a perder cuentas que no son
     * suyas, en la pantalla donde menos se puede exagerar. Sin `agency_id` (backend sin deployar) no hay
     * número: mejor callar un dato cierto que afirmar uno inflado.
     */
    _lostAccounts() {
        const { subscriptionData } = this.props;
        const usage = subscriptionData.usage;
        const used = usage && usage.ad_accounts && usage.ad_accounts.used;
        if (!this._isSharedSubscription() && isNumber(used)) {
            return used;
        }
        const linkedByAgency = subscriptionData.ad_account_linked_by_agency_id || {};
        const ownAgencyId = subscriptionData.agency_id;
        const own = ownAgencyId !== undefined && ownAgencyId !== null
            ? linkedByAgency[ownAgencyId] || linkedByAgency[String(ownAgencyId)]
            : null;
        return (own && own.ad_accounts_linked) || 0;
    }

    /** Los accesos al portal, de `usage`. Con una suscripción compartida no hay un número sólo de esta agencia. */
    _lostPortalAccesses() {
        const { subscriptionData } = this.props;
        const usage = subscriptionData.usage;
        const used = usage && usage.portal_accesses && usage.portal_accesses.used;
        return !this._isSharedSubscription() && isNumber(used) ? used : 0;
    }

    /** «14 líneas de seguimiento en 9 clientes»: dos números, dos claves (copy.md §3). Sin clientes, la primera. */
    _lostLinesText() {
        const { t } = this.props;
        const { lossLines } = this.state;
        if (!lossLines || !(lossLines.lines > 0)) return null;
        const lines = t('agency_subscription.cancel_loss_lines', { count: lossLines.lines });
        return lossLines.clients > 0
            ? `${lines} ${t('agency_subscription.cancel_loss_lines_clients', { count: lossLines.clients })}`
            : lines;
    }

    /**
     * Qué pierde ESTA agencia, con sus números — no "todas las funcionalidades" en abstracto (M5). Un número
     * que no llega no se dibuja.
     */
    _whatIsLost() {
        const { t, subscriptionData } = this.props;
        if (!subscriptionData) {
            return [];
        }

        const items = [];

        const lines = this._lostLinesText();
        if (lines) {
            items.push(lines);
        }
        const accounts = this._lostAccounts();
        if (accounts > 0) {
            items.push(t('agency_subscription.cancel_loss_connected_accounts', { count: accounts }));
        }
        const portal = this._lostPortalAccesses();
        if (portal > 0) {
            items.push(t('agency_subscription.cancel_loss_portal', { count: portal }));
        }

        // Sólo campos que el payload de esta pantalla ya trae (ver `SubscriptionData.data()`).
        const modules = [
            [subscriptionData.looker_cc_enabled, 'agency_subscription.cancel_loss_looker'],
            [subscriptionData.module_dashbo_mcp_enabled, 'agency_subscription.cancel_loss_mcp'],
            [subscriptionData.module_node_to_sheets_enabled, 'agency_subscription.cancel_loss_sheets'],
        ];
        modules.forEach(([enabled, key]) => {
            if (enabled) {
                items.push(t(key));
            }
        });

        return items;
    }

    renderOffRamp() {
        const { t } = this.props;
        const reason = this._selectedReason();
        // Quien vio la oferta y siguió con la baja no recibe otra salida (M5): tres pantallas como mucho.
        if (!reason || !reason.offRamp || this.state.declinedOffer) {
            return null;
        }

        const isPlans = reason.offRamp === 'plans';
        return (
            <div style={{
                backgroundColor: '#F5F5F5',
                borderLeft: '3px solid #FF470F',
                borderRadius: '0.3rem',
                padding: '1em 1.1em',
                marginBottom: '1.2em'
            }}>
                <p style={{
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.95em',
                    color: '#191F29',
                    margin: '0 0 0.35em'
                }}>
                    {t(isPlans
                        ? 'agency_subscription.cancel_offramp_plans_title'
                        : 'agency_subscription.cancel_offramp_talk_title')}
                </p>
                <p style={{
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontSize: '0.87em',
                    color: '#4A4A4A',
                    margin: '0 0 0.8em',
                    lineHeight: 1.5
                }}>
                    {t(isPlans
                        ? 'agency_subscription.cancel_offramp_plans_description'
                        : 'agency_subscription.cancel_offramp_talk_description')}
                </p>
                {/* El wrapper flex NO es decorativo: `.btn` es `display:flex`, o sea una caja de
                    nivel bloque, y suelto dentro de este recuadro se estiraría a todo el ancho —
                    una barra gigante para un CTA secundario. El gotcha está documentado en
                    `_buttons.scss:56-63`; la salida recomendada ahí es justamente acotarlo con un
                    contenedor flex en vez de tocar el `.btn` global. */}
                <div style={{ display: 'flex' }}>
                    <button
                        type="button"
                        className="btn -alt -negative"
                        onClick={isPlans ? this.goToPlans : this.talkToSupport}
                    >
                        {t(isPlans
                            ? 'agency_subscription.cancel_offramp_plans_cta'
                            : 'agency_subscription.cancel_offramp_talk_cta')}
                    </button>
                </div>
            </div>
        );
    }

    renderReasonStep() {
        const { t } = this.props;
        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    <p style={{
                        fontFamily: 'Source Sans Pro, sans-serif',
                        fontSize: '0.95em',
                        color: '#4A4A4A',
                        marginBottom: '1.2em'
                    }}>
                        {t('agency_subscription.cancel_reason_intro')}
                    </p>
                    {/* Opciones de un toque (M2), no un desplegable: los seis motivos a la vista. Radios
                        nativos dentro de un `fieldset` con `legend`, así el lector de pantalla dice la
                        pregunta y las flechas mueven la elección. Los `value` son los de `REASONS`. */}
                    <fieldset className="cancel-sub-modal__reasons">
                        <legend>{t('agency_subscription.cancel_reason')}</legend>
                        {REASONS.map((reason) => {
                            const checked = this.state.reason === reason.value;
                            return (
                                <label key={reason.value}
                                       className={`cancel-sub-modal__reason${checked ? ' -on' : ''}`}>
                                    <input
                                        type="radio"
                                        name="cancel-sub-reason"
                                        value={reason.value}
                                        checked={checked}
                                        onChange={() => this.setState({ reason: reason.value })}
                                    />
                                    <span>{t(reason.labelKey)}</span>
                                </label>
                            );
                        })}
                    </fieldset>
                    <div className="form-row cancel-sub-modal__comments">
                        <label htmlFor="cancel-sub-comments">{t('agency_subscription.comments_optional')}</label>
                        <textarea
                            id="cancel-sub-comments"
                            className="form-control"
                            rows="3"
                            value={this.state.comments}
                            onChange={(e) => this.setState({ comments: e.target.value })}
                            placeholder={t('agency_subscription.cancel_comments_placeholder')}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn -alt -negative" onClick={this.toggle}>
                        {t('agency_subscription.keep_subscription')}
                    </button>
                    <button
                        type="button"
                        className="btn -alt"
                        disabled={!this.state.reason}
                        onClick={this.handleContinue}
                    >
                        {t('agency_subscription.continue')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    renderChecking() {
        const { t } = this.props;
        // A la vista, sólo el Spinner; para el lector de pantalla, un estado con nombre (QA-VIS-bd-fe-21).
        return (
            <ModalBody className="cancel-sub-modal__body cancel-sub-modal__checking" aria-busy="true" role="status">
                <Spinner color="positive" />
                <span className="visually-hidden">{t('common.loading')}</span>
            </ModalBody>
        );
    }

    /**
     * El botón que manda el pedido: mientras envía muestra el spinner, y el verbo queda oculto para el lector
     * de pantalla, así el botón no se queda sin nombre (QA-VIS-bd-fe-02).
     */
    _renderSendingLabel(labelKey) {
        const { t } = this.props;
        if (!this.state.accepting) {
            return t(labelKey);
        }
        return (
            <>
                <Spinner color="negative very-small" />
                <span className="visually-hidden">{t(labelKey)}</span>
            </>
        );
    }

    /**
     * El precio de una oferta: «USD 50 por mes» (+ IVA), con el monto en su propio elemento para darle tamaño.
     * El IVA va adentro del mismo elemento: suelto en la fila flex, el `gap` lo separaba de «por mes».
     */
    _renderOfferPrice(amount, plusVat) {
        const { t } = this.props;
        const { before, after } = splitAround(
            t('agency_subscription.retention_price', { amount: AMOUNT_MARKER, interpolation: RAW }),
            AMOUNT_MARKER
        );
        return (
            <span className="cancel-sub-offer__now">
                {before}<span className="cancel-sub-offer__amount">{amount}</span>{after}
                {plusVat && ` ${t('agency_subscription.retention_plus_vat')}`}
            </span>
        );
    }

    renderOfferStep() {
        const { t, i18n } = this.props;
        const lang = i18n && i18n.language;
        const { offer, accepting, acceptError, quoteChanged } = this.state;
        const reason = this._selectedReason();
        const money = (value) => moneyText(value, offer.currency, lang);

        if (acceptError) {
            return this.renderAcceptError();
        }

        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    <p className="cancel-sub-offer__title">
                        {t(OFFER_TITLE_KEYS[reason && reason.value] || OFFER_TITLE_KEYS.too_expensive)}
                    </p>
                    {quoteChanged && (
                        <p className="cancel-sub-offer__changed" role="status">
                            {t('agency_subscription.retention_quote_changed')}
                        </p>
                    )}
                    <div className="cancel-sub-offer" data-testid="cancel-sub-offer">
                        <p className="cancel-sub-offer__headline">
                            {t('agency_subscription.retention_headline', {
                                percent: offer.percent, count: offer.months, interpolation: RAW
                            })}
                        </p>
                        <p className="cancel-sub-offer__price">
                            {/* El tachado sólo se VE: el lector de pantalla no lo anuncia, así que el precio
                                de hoy lleva su rótulo oculto. */}
                            <s className="cancel-sub-offer__before">
                                <span className="visually-hidden">{t('agency_subscription.retention_price_before')} </span>
                                {money(offer.feeBefore)}
                            </s>
                            {' '}
                            {this._renderOfferPrice(money(offer.feeAfter), offer.plusVat)}
                        </p>
                        <p className="cancel-sub-offer__months">
                            {t('agency_subscription.retention_months', {
                                months: offerMonthsText(offer, lang), interpolation: RAW
                            })}
                        </p>
                        <p className="cancel-sub-offer__savings">
                            {t('agency_subscription.retention_savings', {
                                savings: money(savingsOf(offer)),
                                date: formatLongDate(offer.backToFullOn, lang),
                                full: money(offer.feeBefore),
                                interpolation: RAW
                            })}
                        </p>
                    </div>
                    <p className="cancel-sub-modal__note">{t('agency_subscription.retention_note')}</p>
                    {offer.isAutomaticDebit && (
                        <p className="cancel-sub-modal__note" data-testid="cancel-sub-debit-note">
                            {t('agency_subscription.retention_debit_note')}
                        </p>
                    )}
                </ModalBody>
                {/* Los dos del mismo tamaño: seguir con la baja es tan visible como quedarse. La jerarquía
                    la da el relleno, como en el resto del modal. A lo angosto se apilan, «Quedarme» arriba. */}
                <ModalFooter className="cancel-sub-modal__pair">
                    <button type="button" className="btn -alt -negative" disabled={accepting}
                            onClick={this.handleDeclineOffer}>
                        {t('agency_subscription.retention_decline')}
                    </button>
                    <button type="button" className="btn -alt" disabled={accepting} aria-busy={accepting}
                            onClick={this.handleAcceptOffer}>
                        {this._renderSendingLabel('agency_subscription.retention_accept')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    /** La variante de error de M4: Tesla no aplicó el descuento y no se guardó nada. */
    renderAcceptError() {
        const { t } = this.props;
        const { accepting } = this.state;
        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    <p className="cancel-sub-modal__error" role="alert">{t('agency_subscription.retention_error')}</p>
                </ModalBody>
                <ModalFooter className="cancel-sub-modal__pair">
                    <button type="button" className="btn -alt -negative" disabled={accepting}
                            onClick={this.openRetentionChat}>
                        {t('agency_subscription.retention_error_chat')}
                    </button>
                    <button type="button" className="btn -alt" disabled={accepting} aria-busy={accepting}
                            onClick={this.handleAcceptOffer}>
                        {this._renderSendingLabel('agency_subscription.retention_retry')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    /** M4: el descuento quedó aplicado. Con IVA, cada monto lo dice (P-1, fede 26/10). */
    renderApplied() {
        const { t, i18n } = this.props;
        const lang = i18n && i18n.language;
        const offer = this.state.appliedOffer;
        const vat = offer.plusVat ? ` ${t('agency_subscription.retention_plus_vat')}` : '';
        const money = (value) => `${moneyText(value, offer.currency, lang)}${vat}`;
        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    <div className="cancel-sub-modal__done" role="status">
                        <div className="cancel-sub-modal__done-icon" aria-hidden="true">&#10003;</div>
                        <p className="cancel-sub-modal__done-title">{t('agency_subscription.retention_applied_title')}</p>
                        <p className="cancel-sub-modal__done-body">
                            {t('agency_subscription.retention_applied_body', {
                                months: offerMonthsText(offer, lang),
                                amount: money(offer.feeAfter),
                                date: formatLongDate(offer.backToFullOn, lang),
                                full: money(offer.feeBefore),
                                interpolation: RAW
                            })}
                        </p>
                        <p className="cancel-sub-modal__done-note">{t('agency_subscription.retention_applied_note')}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn -alt -negative" onClick={this.toggle}>
                        {t('agency_subscription.close')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    renderConfirmStep() {
        const { t } = this.props;
        const losses = this._whatIsLost();
        const validTill = this._validTill();

        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    {this.renderOffRamp()}

                    <p style={{ color: '#dc3545', fontWeight: 600, marginBottom: '0.8em' }}>
                        {t('agency_subscription.cancel_warning')}
                    </p>

                    {losses.length > 0 && (
                        <>
                            <p style={{
                                fontFamily: 'Source Sans Pro, sans-serif',
                                fontSize: '0.87em',
                                color: '#4A4A4A',
                                marginBottom: '0.4em'
                            }}>
                                {t('agency_subscription.cancel_what_you_lose')}
                            </p>
                            <ul className="cancel-sub-modal__losses" style={{
                                fontFamily: 'Source Sans Pro, sans-serif',
                                fontSize: '0.87em',
                                color: '#4A4A4A',
                                paddingLeft: '1.2em',
                                marginBottom: '1em',
                                lineHeight: 1.6
                            }}>
                                {losses.map((loss) => <li key={loss}>{loss}</li>)}
                            </ul>
                        </>
                    )}

                    {validTill && (
                        <p style={{
                            fontFamily: 'Source Sans Pro, sans-serif',
                            fontSize: '0.87em',
                            color: '#B6B6B7',
                            margin: 0
                        }}>
                            {t('agency_subscription.cancel_active_until', {
                                date: validTill,
                                interpolation: { escapeValue: false }
                            })}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter>
                    {/* Volver al motivo es empezar de nuevo: con otro motivo, su salida vuelve a valer. */}
                    <button type="button" className="btn -alt -negative"
                            onClick={() => this.setState({ step: STEP_REASON, declinedOffer: false })}>
                        {t('agency_subscription.back')}
                    </button>
                    {this.state.sending
                        ? <Spinner color="positive small" />
                        : <button type="button" className="btn -alt" onClick={this.handleSubmit}>
                            {t('agency_subscription.confirm_cancel')}
                          </button>
                    }
                </ModalFooter>
            </>
        );
    }

    renderSent() {
        const { t } = this.props;
        const validTill = this._validTill();

        return (
            <>
                <ModalBody className="cancel-sub-modal__body">
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#198754' }}>&#10003;</div>
                        <p>{t('agency_subscription.cancellation_sent')}</p>
                        {/* Lo que faltaba y producía el "apreté cancelar y no pasó nada": decir en
                            voz alta que la suscripción NO se dio de baja sola. */}
                        <p style={{ fontSize: '0.87em', color: '#4A4A4A', marginTop: '0.6em' }}>
                            {validTill
                                ? t('agency_subscription.cancellation_still_active_until', {
                                    date: validTill,
                                    interpolation: { escapeValue: false }
                                })
                                : t('agency_subscription.cancellation_still_active')}
                        </p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn -alt -negative" onClick={this.toggle}>
                        {t('agency_subscription.close')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    render() {
        // `triggerLabel` / `triggerClassName`: la vista de la agencia paga lo dispara con un link discreto
        // («Dar de baja la suscripción») en vez del botón. El modal es el mismo.
        const { t, hideTrigger, triggerLabel, triggerClassName, modalClassName } = this.props;
        const { isOpen, sent, step } = this.state;

        return (
            <>
                {/* Sistema de botones PROPIO del repo, no el de Bootstrap: BS se importa antes
                    que `_buttons.scss` y sus `.btn-outline-danger` / `.btn-sm` de 5.3 sólo setean
                    custom properties, así que el `.btn` de Dashbo les ganaba y el botón salía
                    ciruela sólido, en mayúsculas y de 207px de ancho. `-alt -negative` es el
                    outline del sistema — el mismo que usa el CTA del off-ramp de acá arriba.

                    Vale para TODO el modal, no sólo para este disparador: los `<Button color=...>`
                    de reactstrap de los tres footers emitían `btn btn-secondary` / `btn btn-danger`
                    y salían los cuatro iguales (naranja sólido, en mayúsculas), o sea "Volver" y
                    "Confirmar cancelación" indistinguibles. Ahora el par se lee por RELLENO:
                    `-alt -negative` (outline) para la salida —mantener, volver, cerrar— y `-alt`
                    (sólido) para la acción que avanza. `_buttons.scss` NO tiene una variante
                    destructiva/roja y acá no se inventa una: la jerarquía la da el relleno, no el
                    color. */}
                {/* `hideTrigger` esconde el DISPARADOR sin desmontar el modal. Lo prende la sección
                    cuando ya hay una solicitud en curso. Si en vez de esto se dejara de renderizar
                    el componente entero, confirmar la baja lo desmontaría en el mismo tick y la
                    pantalla de éxito no se vería nunca. */}
                {!hideTrigger && (
                    <button
                        type="button"
                        className={triggerClassName || 'btn -alt -negative'}
                        onClick={this.toggle}
                    >
                        {triggerLabel || t('agency_subscription.cancel_subscription')}
                    </button>
                )}
                {/* `modalClassName`: la vista de la agencia paga le pone el marco de sus otros modales, así el
                    título se ve igual en los tres (QA-VIS-spfe-41). Sin ella, el de siempre. */}
                <Modal isOpen={isOpen} toggle={this.toggle} className={modalClassName}>
                    <ModalHeader toggle={this.toggle}>
                        {t('agency_subscription.cancel_subscription_title')}
                    </ModalHeader>
                    {sent ? this.renderSent() : this.renderStep(step)}
                </Modal>
            </>
        );
    }

    renderStep(step) {
        switch (step) {
            case STEP_REASON:
                return this.renderReasonStep();
            case STEP_CHECKING:
                return this.renderChecking();
            case STEP_OFFER:
                return this.renderOfferStep();
            case STEP_APPLIED:
                return this.renderApplied();
            default:
                return this.renderConfirmStep();
        }
    }
}

export default withTranslation()(CancelSubscriptionModal);
export { REASONS };
